<template>
  <div class="col-span-12 space-y-4">
    <div class="flex items-center gap-2 -mb-2">
      <div
        v-if="cardError?.card"
        id="card-error"
        class="text-red-500 flex items-center gap-1"
      >
        <WarningIcon class="w-4 h-4" />
        <BaseText
          size="xs"
        >
          {{ cardError.card }}
        </BaseText>
      </div>
      <div
        v-if="cardError?.address"
        id="card-error"
        class="text-red-500 flex items-center gap-1"
      >
        <WarningIcon class="w-4 h-4" />
        <BaseText
          size="xs"
        >
          {{ cardError.address }}
        </BaseText>
      </div>
    </div>
    <div class="space-y-1">
      <BaseText
        type="label"
        size="sm"
        class="text-text-muted"
      >
        Card Number
      </BaseText>
      <div
        id="card-number"
        class="form-input fade-in min-w-0 w-full px-2 py-1.5"
      />
    </div>

    <div class="grid grid-cols-2 gap-3">
      <div class="space-y-1">
        <BaseText
          type="label"
          size="sm"
          class="text-text-muted"
        >
          Expiration Date
        </BaseText>
        <div
          id="card-expiry"
          class="form-input fade-in min-w-0 w-full px-2 py-1.5"
        />
      </div>

      <div class="space-y-1">
        <BaseText
          type="label"
          size="sm"
          class="text-text-muted"
        >
          CVC
        </BaseText>
        <div
          id="card-cvc"
          class="form-input fade-in min-w-0 w-full px-2 py-1.5"
        />
      </div>
    </div>

    <div class="space-y-1 z-50 relative">
      <BaseText
        type="label"
        size="sm"
        class="text-text-muted"
      >
        Billing Address
      </BaseText>
      <LocationAutocomplete
        :error="cardError?.address ? cardError.address.message : null"
        :value="billingAddress"
        @updateText="handleBillingAddressChange"
        @addressSelect="updateBillingAddress"
        @blur="onLocationBlur"
      />
    </div>
  </div>
</template>

<script>
import LocationAutocomplete from '../../settings/LocationAutocomplete.vue'
import WarningIcon from '../../globals/Icons/WarningIcon.vue'
export default {
  components: {
    LocationAutocomplete,
    WarningIcon
  },
  props: {
    intentSecret: {
      type: String,
      required: true
    },
    cardBillingAddress: {
      type: Object,
      required: true
    }
  },
  data () {
    const testPk = 'pk_test_51JS7DeAQtq0TW7KZz4oPmzFVgxlULjWuUNKBoo0KOJjVi90wLS3VniuKgSLJHNHeFBNuY6f8jP9mD3bLygTj8BRM00aL3PiZeq'
    const livePk = 'pk_live_51JS7DeAQtq0TW7KZxoGv8Spu6IJhR335IoLxdfBQMF8VCbWJFDrUl2DfeNJllyC79mZJv8kX0qiaNGFCgbrTznpA00kPK2gMwN'
    return {
      stripeKey: process.env.NODE_ENV === 'development' ? testPk : livePk,

      token: null,
      cardNumber: null,
      cardExpiry: null,
      cardCvc: null,
      cardError: {
        address: null,
        card: null
      },
      cardComplete: {
        cardNumber: false,
        cardExpiry: false,
        cardCvc: false
      },

      // String for location autocomplete
      billingAddress: '',
      didSelectBillingAddress: false,
      isBillingAddressValid: false
    }
  },
  computed: {
    stripeElements () {
      return this.$stripe.elements()
    },
    isFormComplete () {
      const isCardComplete = Object.values(this.cardComplete).every(value => value)
      return this.cardError.card === null && this.cardError.address === null && this.didSelectBillingAddress && isCardComplete
    }
  },
  mounted () {
    // Style docs: https://stripe.com/docs/js/appendix/style
    const style = {
      base: {
        color: '#06070F',
        transition: 'color 150ms ease-in-out',
        '::placeholder': {
          color: 'rgba(94, 102, 120, 0.9)'
        },
        ':hover::placeholder': {
          color: '#303546'
        },
        ':focus::placeholder': {
          color: '#C1C7D0'
        }
      },
      invalid: {
        color: '#ef4444',
        iconColor: '#ef4444'
      }
    }

    // Create card elements
    this.cardNumber = this.stripeElements.create('cardNumber', { style })
    this.cardExpiry = this.stripeElements.create('cardExpiry', { style })
    this.cardCvc = this.stripeElements.create('cardCvc', { style })

    // Mount elements
    this.cardNumber.mount('#card-number')
    this.cardExpiry.mount('#card-expiry')
    this.cardCvc.mount('#card-cvc')

    // Add event listeners
    this.cardNumber.on('change', this.handleCardChange)
    this.cardExpiry.on('change', this.handleCardChange)
    this.cardCvc.on('change', this.handleCardChange)
  },
  beforeDestroy () {
    if (this.cardNumber) {
      this.cardNumber.destroy()
    }
    if (this.cardExpiry) {
      this.cardExpiry.destroy()
    }
    if (this.cardCvc) {
      this.cardCvc.destroy()
    }
  },
  methods: {
    resetCardError (type) {
      if (type === 'card') {
        this.cardError.card = null
      } else if (type === 'address') {
        this.cardError.address = null
      }
    },
    onLocationBlur () {
      this.isBillingAddressValid = this.didSelectBillingAddress
      if (!this.isBillingAddressValid && this.billingAddress.length > 0) {
        this.cardError.address = 'Your location is incomplete.'
      }
    },
    handleBillingAddressChange (val) {
      this.resetCardError('address')
      this.billingAddress = val
      this.didSelectBillingAddress = false
      this.$emit('statusChange', this.isFormComplete)
    },
    updateBillingAddress (payload) {
      const { city, country, postalCode, state, street } = payload
      this.$emit('update:cardBillingAddress', {
        ...this.cardBillingAddress,
        city: city || '',
        country: country || '',
        state: state || '',
        street: street || '',
        postal_code: postalCode || ''
      })
      this.didSelectBillingAddress = true
      this.resetCardError('address')
      this.$emit('statusChange', this.isFormComplete)
    },
    async confirmSetup (billingDetails = {}) {
      try {
        this.cardError.card = null

        const { setupIntent, error } = await this.$stripe.confirmCardSetup(
          this.intentSecret,
          {
            payment_method: {
              card: this.cardNumber,
              billing_details: {
                ...billingDetails,
                address: {
                  line1: this.cardBillingAddress.street,
                  city: this.cardBillingAddress.city,
                  state: this.cardBillingAddress.state,
                  postal_code: this.cardBillingAddress.postal_code,
                  country: this.cardBillingAddress.country
                }
              }
            }
          }
        )

        if (error) {
          this.cardError.card = error.message
          console.error('Setup intent error:', error)
          return { error }
        } else {
          console.log('Setup intent successful:', setupIntent)
          return { setupIntent }
        }
      } catch (error) {
        this.cardError.card = error.message || 'An unexpected error occurred'
        console.error('Confirm setup error:', error)
        return { error }
      }
    },

    handleCardChange (event) {
      this.cardComplete[event.elementType] = event.complete
      if (!event.error) {
        this.resetCardError('card')
      } else {
        this.cardError.card = event.error.message
      }
      this.$emit('statusChange', this.isFormComplete)
    }
  }
}
</script>

<style scoped>
.form-input {
  color: #06070F;
  border-radius: 6px;
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.08), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  transition: box-shadow 150ms ease-in-out;
}
.form-input:focus-within {
  outline: none;
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.1), 0px 0px 0px 1px rgba(0, 56, 108, 0.1);
}
</style>
